import { onBeforeUnmount, onMounted, watch, type Ref } from 'vue';

/** The sticky header the assistant has to clear. It wraps the topline, any banner and #header. */
const HEADER_SELECTOR = '.ak-header';

const INSET_PROPERTY = '--search-ai-inset-top';

/**
 * Keeps the assistant's full-screen window clear of the sticky header.
 *
 * `.ak-header` is sticky at a higher stacking order than the window, so whenever
 * the plugin fills the viewport — a narrow screen, or Expand on a wide one — it
 * paints over the window's own header and the Assistant/History tabs.
 *
 * Measured, not assumed: the header wraps the topline, the impersonation banner
 * and `#header`, so its height moves with all three. `bottom` rather than
 * `height`, so anything sitting above it is counted too.
 *
 * The flag gates the published value, not the measurement. An earlier version
 * skipped setup entirely until the brand assistant was enabled, so a
 * measurement missed while it was off never recovered and Expand stayed broken;
 * an ungated one went the other way and left the property set on retailer
 * pages, where the retailer agent's own full-screen rule reads it — insetting a
 * window this feature does not own. The observer below always runs; only what
 * it writes depends on `enabled`.
 */
export const useAssistantInset = (enabled: Ref<boolean>): void => {
  let observer: ResizeObserver | undefined;

  const apply = (): void => {
    if (!enabled.value) {
      document.documentElement.style.removeProperty(INSET_PROPERTY);

      return;
    }

    const header = document.querySelector<HTMLElement>(HEADER_SELECTOR);
    const inset = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;

    document.documentElement.style.setProperty(INSET_PROPERTY, `${inset}px`);
  };

  // The account and the route resolve after mount, so the flag is routinely
  // still false when the measurements below first run.
  watch(enabled, apply);

  onMounted(() => {
    apply();

    // Re-measured after paint: on a blade page the header is in the DOM before
    // Vue mounts, but web fonts and late banners can still change its height.
    requestAnimationFrame(apply);

    const header = document.querySelector<HTMLElement>(HEADER_SELECTOR);
    if (header && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(apply);
      observer.observe(header);
    }

    window.addEventListener('resize', apply);
  });

  onBeforeUnmount(() => {
    observer?.disconnect();
    window.removeEventListener('resize', apply);
    // Left behind it would inset a window this app is no longer showing.
    document.documentElement.style.removeProperty(INSET_PROPERTY);
  });
};
